import ApiController from './ApiController';
import PermissaoController from './PermissaoController';
import Avaliador from '../models/Avaliador';

/**
 * Lista de avaliadores retornada pela API com informações de paginação
 * @typedef Listagem
 * @property {Boolean} sucesso Indica se a requisição foi executada
 * @property {Array<Erro>} erros Listagem dos erros retornados
 * @property {Array<Avaliador>} items Em caso de sucesso contém os avaliadores retornados
 * @property {Number} page Número da página
 * @property {Number} count Quantidade total de registros
 * @property {Number} pageCount Quantidade de páginas disponíveis
 * @property {Number} pageSize Quantidade de registros por página
 */

/**
 * Retorno dos métodos
 * @typedef Retorno
 * @property {Boolean} sucesso Indica se o método foi executado com êxito
 * @property {Avaliador} avaliador Dados do avaliador
 */

/**
 * Retorno da listagem sem paginação
 * @typedef RetornoSelecao
 * @property {Boolean} sucesso Indica se a requisição foi executada
 * @property {Array<Avaliador>} items Em caso de sucesso contém os avaliadores retornados
 */

/**
 * Controller para cadastro de avaliadores
 */
class AvaliadorController {

    constructor() {
        this.apiController = new ApiController();
        this.permissaoController = new PermissaoController();
    }

    /**
     * Lista os avaliadores cadastrados
     * @param {Number} page Número da página
     * @param {Number} pageSize Quantidade de registros por página
     * @param {Object} params Parâmetros extras de pesquisa
     * @param {String} params.nome Pesquisar um avaliador por nome
     * 
     * @return {Promise<RetornoListar>} Retorna as avaliações encontradas
     */
    async listar(page, pageSize, { nome }) {
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.get('/avaliador', { page, pageSize, nome });
            //Marca que houve retorno com sucesso
            result.sucesso = true;
            //Mapeia os itens para objetos do tipo Avaliacao
            const items = result.items.map(item => Avaliador.clone(item));
            result.items = items;
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Retorna os dados de um avaliador específica, com as seções e questões relacionadas
     * @param {Number} id Id do avaliador a ser consultado
     * 
     * @return {Promise<Retorno>} Retorna o avaliador solicitado
     */
    async obter(id) {
        //Verifica se o ID está preenchido
        if (!id)
            return { sucesso: false, erros: [ { campo: 'id', mensagem: 'É necessário' } ] };
        
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.get('/avaliador/' + id.toString());
            //Retorna indicando que houve sucesso e os dados do avaliador
            return { sucesso: true, avaliador: Avaliador.clone(result) };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Inclui um novo avaliador
     * @param {Avaliador} avaliador Dados do avaliador a ser incluído
     * 
     * @returns {Promise<Retorno>} Retorna o avaliador inserido, com as permissões preenchidas
     */
    async salvar(avaliador) {
        const erros = await this._validarCampos(avaliador, false);
            
        //Retorna os erros para a aplicação
        if (erros.length > 0)
            return { sucesso: false, erros };

        try {
            //Monta o corpo da requisição
            const body = await this._extrairCampos(avaliador);
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.post('/avaliador', body);
            //Retorna indicando sucesso e cria uma cópia da avaliação
            return { sucesso: true, avaliador: Avaliador.clone(result) };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    /**
     * Atualiza os dados de um avaliador
     * @param {Avaliador} avaliador Dados do avaliador a ser incluído
     * 
     * @returns {Promise<RetornoSelecao>} Retorna a lista de avaliadores encontrados
     */
    async atualizar(avaliador) {
        const erros = await this._validarCampos(avaliador, true);
            
        if (erros.length > 0)
            return { sucesso: false, erros };

        try {
            //Monta o corpo da requisição
            const body = await this._extrairCampos(avaliador);
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: result } = await this.apiController.put('/avaliador/' + avaliador.id.toString(), body);
            //Retorna indicando sucesso e cria uma cópia da avaliação
            return { sucesso: true, avaliador: Avaliador.clone(result) };
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }


    /**
     * Lista os avaliadores de uma determinada empresa, sem os dados de paginação
     * @param {Number} empresa Identificação da empresa para listagem
     * 
     * @returns {Promise<Array<Avaliador>>} Retorna uma lista de avaliadores
     */
    async listarSelecao(empresa) {
        try {
            //Envia requisição para o servidor e captura os dados de resposta
            const { data: items } = await this.apiController.get('/avaliador', { pagination: false, empresa });
            //Marca que houve retorno com sucesso
            const result = {
                sucesso: true,
                items
            };
            return result;
        } catch (error) {
            //Captura o erro e retorna para a aplicação
            return { sucesso: false, erros: error.response.data };
        }
    }

    async _validarCampos(avaliador, atualizacao) {
        var erros = [];

        //Verifica se a empresa está sendo preenchida
        if (!avaliador) {
            erros.push({ campo: '', mensagem: 'Avaliador é necessário' });
        } else {
            //Verifica se o id está preenchido
            if (atualizacao && !avaliador.id) 
                erros.push({ campo: 'id', mensagem: 'É necessário' });
            
            //Verifica se o nome está preenchido
            if (!avaliador.nome)
                erros.push({ campo: 'nome', mensagem: 'É necessário' });
            
            //Verifica se o email ou telefone do usuário estão preenchidos
            if (!avaliador.email && !avaliador.telefone) {
                erros.push({ campo: 'email', mensagem: 'É necessário' });
                erros.push({ campo: 'telefone', mensagem: 'É necessário' });
            }
            
            //Verifica se a senha está preenchida
            if (!atualizacao && !avaliador.senha) 
                erros.push({ campo: 'senha', mensagem: 'É necessário' });
            
            if (!avaliador.permissoes)
                erros.push({ campo: 'permissoes', mensagem: 'É necessário' });
            else if (!Array.isArray(avaliador.permissoes))
                erros.push({ campo: 'permissoes', mensagem: 'Precisa ser um array' });
            
            //Verifica se o id da empresa está preenchida
            if (await this.permissaoController.permiteSelecionarEmpresa()) {
                if (!avaliador.empresa)
                    erros.push({ campo: 'empresa', mensagem: 'É necessário' });
            }
        }


        return erros;
    }

    /**
     * Extrai as informações para o envio para a API
     * @param {Avaliador} avaliador Dados do avaliador a ser inserido
     * @returns {Object} Retorna apenas os dados necessários para a requisição
     */
    async _extrairCampos(avaliador) {
        const result = {
            nome: avaliador.nome,
            permissoes: avaliador.permissoes.map(permissao => permissao.id)
        };
        
        if (avaliador.senha)
            result.senha = avaliador.senha;
        if (avaliador.email)
            result.email = avaliador.email;
        if (avaliador.telefone)
            result.telefone = avaliador.telefone;

        if (await this.permissaoController.permiteSelecionarEmpresa()) {
            if (avaliador.empresa)
                result.empresa = avaliador.empresa.id;
        }

        return result;
    }

}

export default AvaliadorController;